import React from 'react';
import { useMatch, useRouter } from '@tanstack/react-location';

// Medium Blog Card
const MediumBlogCard = ({ imageURL, title, link }) => (
    <div className="transition-transform transform hover:scale-105 duration-200 h-full">
        <a
            href={link}
            target="_blank"
            rel="noreferrer noopener"
            className="glassIco flex flex-col justify-between h-full rounded-lg shadow-lg overflow-hidden"
        >
            <img className="object-cover w-full h-48" src={imageURL} alt={title} />
            <div className="p-4 flex flex-col justify-between h-full">
                <h5 className="text-xl font-semibold text-neutral-800 dark:text-neutral-50 line-clamp-2">
                    {title}
                </h5>
                <div className="flex justify-end mt-auto pt-4">
          <span className="rounded bg-navSpecial px-4 py-2 text-sm font-medium text-navbarBg hover:bg-opacity-90 transition">
            Read more
          </span>
                </div>
            </div>
        </a>
    </div>
);

// Instagram Embed Card
const InstagramCard = ({ postUrl }) => {
    const postId = postUrl.split('/p/')[1]?.replace(/\/$/, '');
    const embedUrl = `https://www.instagram.com/p/${postId}/embed/captioned/`;

    return (
        <div className="transition-transform transform hover:scale-105 duration-200 h-full">
            <div className="glassIco flex flex-col justify-between h-full rounded-lg shadow-lg overflow-hidden">
                <div className="relative overflow-hidden" style={{ paddingBottom: '100%' }}>
                    <iframe
                        src={embedUrl}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        scrolling="no"
                        allowTransparency="true"
                        allow="encrypted-media"
                        title="Instagram Embed"
                        className="absolute top-0 left-0 w-full h-full"
                        style={{
                            position: 'absolute',
                            top: '-60px',
                            left: 0,
                            width: '100%',
                            height: 'calc(100% + 50px)',
                        }}
                        onLoad={(e) => {
                            const iframe = e.target;
                            setTimeout(() => {
                                try {
                                    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                                    const header = iframeDoc.querySelector('header');
                                    if (header) header.style.display = 'none';
                                } catch (error) {
                                    console.log('Could not modify iframe content');
                                }
                            }, 1000);
                        }}
                    ></iframe>
                </div>
                <div className="p-4 flex justify-end">
                    <a
                        href={postUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="rounded bg-navSpecial px-4 py-2 text-sm font-medium text-navbarBg hover:bg-opacity-90 transition"
                    >
                        Read more
                    </a>
                </div>
            </div>
        </div>
    );
};

function MediumBlog() {
    const { data } = useMatch();
    const router = useRouter();

    if (router.pending) {
        return (
            <div className="flex items-center justify-center space-x-2 animate-pulse h-screen w-screen">
                <div className="w-40 h-40 border-t-4 border-b-4 border-green-900 rounded-full animate-spin text-navSpecial"></div>
            </div>
        );
    }

    if (data.blogs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-screen w-screen">
                <h1 className="font-semibold text-xl md:text-5xl text-navSpecial">
                    No blogs present! Stay tuned for more updates
                </h1>
            </div>
        );
    }

    const instagramPosts = [
        'https://www.instagram.com/p/DC9A91eSnYF',
        'https://www.instagram.com/p/DFPspltS3E1',
        'https://www.instagram.com/p/DEH91fcymdy',
        'https://www.instagram.com/p/C-YE0lSoOCd',
        'https://www.instagram.com/p/DAyH_7Hy8i1',
    ];

    const allPosts = [
        ...data.blogs.map((blog) => ({
            type: 'medium',
            imageURL: blog.description?.toString().match(/<img[^>]+src="([^">]+)"/)?.[1] || '/fallback.jpg',
            title: blog.title,
            link: blog.link,
        })),
        ...instagramPosts.map((url) => ({
            type: 'instagram',
            url,
        })),
    ];

    return (
        <div className="pt-[7em] px-4 md:px-7 pb-7">
            {/* Updated Header */}
            <div className="text-center mb-6">
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
                    OUR BLOGS
                </h2>
                <p className="mt-2 text-gray-500">Explore our articles and posts</p>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
                {allPosts.map((post, index) =>
                    post.type === 'medium' ? (
                        <MediumBlogCard
                            key={`medium-${index}`}
                            imageURL={post.imageURL}
                            title={post.title}
                            link={post.link}
                        />
                    ) : (
                        <InstagramCard key={`instagram-${index}`} postUrl={post.url} />
                    )
                )}
            </div>
        </div>
    );
}
export default MediumBlog;
